import EquipmentIcon from '@/assets/icons/CarDataIcon';
import DashboardIcon from '@/assets/icons/DashboardIcon';
import IMPORTIcon from '@/assets/icons/IMPORTIcon';
import LEASINGIcon from '@/assets/icons/LEASINGIcon';
import TAXIcon from '@/assets/icons/TAXIcon';
import SubLogo from '@/components/SubLogo';
import { companyConfig } from '@/config';
import CookieDeclarationButton from '@/features/CookieDeclaration';
import FloatingShortcutButtons from '@/features/FloatingShortcuts';
import SupportMessageForm from '@/features/SupportMessageForm';
import { useLogout } from '@/hooks/useAuth';
import { RecommendationStateItem, useRecommendationsState } from '@/hooks/useRecommendationsState';
import useSurvicate from '@/hooks/useSurvicate';
import { getUser } from '@/services/auth';
import { searchSystem } from '@/services/hub';
import { formatDate, formatPhone } from '@/utils/string';
import { debounce } from '@/utils/utils';
import { useUrgentSystemNews } from '@/hooks/useSystemNews';
import DMFPROLogoWhite from '@assets/DMF-Logo/DMFPRO/Horizontal/dmf-white2x.png';
import DMFPROIconLight from '@assets/DMF-Logo/DMFPRO/Vertical/DMF-V-Color.png';
import Avatar from '@components/Avatar';
import ContentLoading from '@components/ContentLoading';
import NotFound from '@components/NotFound';
import { Button, Dialog, DialogBackdrop, DialogPanel, Menu, MenuButton, MenuHeading, MenuItem, MenuItems, MenuSection, MenuSeparator, Popover, PopoverButton, PopoverPanel, Transition, TransitionChild } from '@headlessui/react';
import {
    ArrowPathIcon,
    ArrowRightStartOnRectangleIcon,
    AtSymbolIcon,
    Bars3Icon,
    BuildingOfficeIcon,
    CalendarDaysIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    EnvelopeIcon,
    MagnifyingGlassIcon,
    PhoneIcon, PlusIcon,
    ScaleIcon,
    TicketIcon,
    UserIcon, UserPlusIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { useProfile } from '@hooks/useProfile';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, createFileRoute, redirect, useLoaderData, useLocation, useRouterState } from '@tanstack/react-router';
import { errorMessage } from '@utils/toast';
import clsx from 'clsx';
import { t } from 'i18next';
import { ChangeEvent, ComponentPropsWithoutRef, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import InviteNewUser from "@features/dealer/InviteNewUser.tsx";
import StatusTag from "@components/StatusTag.tsx";
import {User} from "../types/auth.ts";
import KimImage from '@assets/images/Kim-Asferg-Mikkelsen.png';


// This "cachedUser" provides a poor-mans debouncing solution to make sure that the application
// does not spam the backend with constant "whoAmI" requests.
// Also, there is a "useUser" hook that is not used anywhere?
let cachedUser: number | null = null;
const cacheTimeoutMs = 60 * 1000;
const DESKTOP_SIDEBAR_COLLAPSED_WIDTH_CLASS = 'lg:w-16';
const DESKTOP_SIDEBAR_EXPANDED_WIDTH_CLASS = 'lg:w-56';
const DESKTOP_CONTENT_COLLAPSED_OFFSET_CLASS = 'lg:pl-16';
const DESKTOP_CONTENT_EXPANDED_OFFSET_CLASS = 'lg:pl-56';
const RECOMMENDATION_PLACEHOLDER_IMAGE_URL = DMFPROIconLight;

type RecommendationListItemProps = {
    recommendation: RecommendationStateItem;
    isRead: boolean;
    onOpen: (recommendation: RecommendationStateItem) => void;
} & ComponentPropsWithoutRef<'button'>;

type InviteUserOpenSetter = (open: boolean) => void;

type RecommendationDetailsDialogProps = {
    recommendation: RecommendationStateItem | null;
    onClose: () => void;
    setInviteUserOpen: InviteUserOpenSetter;
    onInviteUser?: () => void;
};

const BASE_RECOMMENDATIONS: RecommendationStateItem[] = [
    {
        id: 'create-user',
        title: 'Lav en bruger til dine sælgere (gratis)',
        body: 'I forbindelse med din adgang til DMF Pro, har du ubegrænset brugere. Det betyder, at hver sælger eller administrative medarbejder, kan få egen bruger. Fordelen er, at hver sag lander i den rette indbakke. I kan naturligvis se hinandens sager på tværs af virksomheden.',
        imageUrl: RECOMMENDATION_PLACEHOLDER_IMAGE_URL,
        imageObjectFitContain: true,
        date: '2026-05-05',
        link: '#',
        linkText: 'Inviter din kollega her',
        inviteUser: true,
    },
    {
        id: 'browser-extension',
        title: 'Få lettere ved at oprette TAX-sager.',
        body: 'Installér browserudvidelse og få lettere ved at oprette TAX-sager.\n\n' +
            'Opret TAX-Pro sager viser et lille DMF-Pro logo på hjemmesider med professionelle bildata.\n\n' +
            'Med et enkelt klik overføres stelnummer, reg.dato, KM eller link for den pågældende bil til en TAX-Pro sagsoprettelse i DMF-Pro.\n\n' +
            'Slut med at kopiere stelnumre, formatere registreringsdatoer eller holde styr på faner og annoncer – 1 klik og så er TAX-Pro sagen oprettet!\n\n' +
            'DMF-Pro: Opret TAX-Pro sager med 1 klik fungerer med alle web-sites der indeholder bildata i form af stelnumre, registreringsdato og kilometer.\n\n' +
            'Derudover er den særligt udvidet til at fungere med de vigtigste sider for bilforhandlere: Bilinfo, Mobile.de, AutoProff/AutoScout24, BCA, og flere.',
        imageUrl: RECOMMENDATION_PLACEHOLDER_IMAGE_URL,
        imageObjectFitContain: true,
        link: 'https://chromewebstore.google.com/detail/dmf-pro-opret-tax-pro-sag/jpjkgpcjbenbkimpnffpgnlkaaldjpjd?authuser=1&hl=en',
        linkText: 'Download nu',
        target: '_blank',
        date: '2026-05-05',
    },
    {
        id: 'tax-walkthrough',
        title: 'Får I det optimale ud af Tax produktet?',
        body: 'Kim har siddet med hundredvis af forhandlere og ved hvad der virker, men også hvad der ikke gør. \n\n' + 'Vi giver nu muligheden for, at du kan booke et møde med os, så kigger vi med på jeres setup og giver sparring på hvordan I får mere ud af DMF Pro.',
        imageUrl: KimImage,
        link: 'mailto:kam@danskmotorfinans.dk?subject=Book%20demo%20anmodning%20i%20DMF%20Pro',
        linkText: 'Book en demo',
        secondaryLink: 'tel:+4540183884',
        secondaryLinkText: 'Ring til Kim på +45 4018 3884',
        date: '2026-05-05',
    },
];

function hasActiveSubscription(user: User) {
    return user?.subscription?.details?.isActive ?? false;
}

function getRecommendations(user: User): RecommendationStateItem[] {
    if (hasActiveSubscription(user)) {
        return BASE_RECOMMENDATIONS;
    }

    return [
        {
            id: 'tax-control-review',
            title: 'Klar til kontrol fra Motorstyrelsen?',
            body: 'Hvis en afgiftsberegning bliver udtaget til kontrol, er det afgørende, at dokumentationen er i orden. Vi sikrer, at jeres sager er korrekt beregnet og dokumenteret – så slipper i for reguleringer fra Motorstyrelsen.',
            imageUrl: KimImage,
            link: 'tel:+4540183884',
            linkText: 'Ring til Kim og book en gennemgang.',
            date: '2026-05-05',
        },
        ...BASE_RECOMMENDATIONS,
    ];
}

export const Route = createFileRoute('/_protected')({
    pendingComponent: ContentLoading,
    notFoundComponent: NotFound,
    loader: async ({ location }) => {
        try {
            const data = await getUser()
            if (!data) {
                throw "redirect to login"
            }

            cachedUser = window.setTimeout(() => cachedUser = null, cacheTimeoutMs);

            return {
                // Do the Data Dance
                user: data.data.data
            }
        } catch (error) {
            throw redirect({
                to: '/login',
                search: {
                    redirect: location.href,
                },
            })
        }
    },
    shouldReload: () => cachedUser === null,
    component: AuthLayout,
})


function AuthLayout() {
    let isPageWithEmbeddedIframe = false;
    const [isDesktopSidebarExpanded, setIsDesktopSidebarExpanded] = useState(true);
    const [inviteUserOpen, setInviteUserOpen] = useState(false)

    // useIntercom()
    useSurvicate();

    const path = useLocation().pathname

    if (path.includes('/import') || path.includes('/leasing') || path.includes('/equipment')) {
        isPageWithEmbeddedIframe = true;
    }

    // sep 30 2024: Disabled Sentry feedback. Large overlap with similar feature 'Send en besked'
    // useEffect(() => {
    //     if (profile) {
    //         Sentry.setUser({
    //             fullName: profile?.data.name,
    //             email: profile?.data.email,
    //         });
    //     }
    // }, [profile])
    // useEffect(() => {
    //     Sentry.addIntegration(
    //         Sentry.feedbackIntegration({
    //             // Additional SDK configuration goes in here, for example:
    //             showBranding: true,
    //             triggerLabel: "Feedback",
    //             triggerAriaLabel: "Giv os feedback",
    //             formTitle: "Giv os feedback",
    //             nameLabel: "Navn",
    //             namePlaceholder: "Dit navn",
    //             emailLabel: "Din email",
    //             messagePlaceholder: "Fortæl os din mening",
    //             messageLabel: "Din mening",
    //             successMessageText: "Tak for din feedback",
    //             addScreenshotButtonLabel: "Tilføj screenshot",
    //             removeScreenshotButtonLabel: "Fjern screenshot",
    //             submitButtonLabel: "Send feedback",
    //             isRequiredLabel: "*",
    //             cancelButtonLabel: "Annuler",
    //             confirmButtonLabel: "Bekræft",

    //             colorScheme: "system",
    //             useSentryUser: {
    //                 name: "fullName",
    //                 email: "email",
    //             },
    //             themeLight: {
    //                 background: "#f5f7f9",
    //             },
    //             themeDark: {
    //                 background: "#082034",
    //             },
    //         })
    //     )
    // }, [])

    return (
        <>
            <div className='min-h-full flex-1 flex flex-col'>
                <AuthHeader
                    isDesktopSidebarExpanded={isDesktopSidebarExpanded}
                    setInviteUserOpen={setInviteUserOpen}
                />
                <MobileSidebar setInviteUserOpen={setInviteUserOpen} />

                <DesktopSidebar
                    isExpanded={isDesktopSidebarExpanded}
                    onToggle={() => setIsDesktopSidebarExpanded(current => !current)}
                />

                <div className={clsx(
                    'flex-1 flex flex-col transition-[padding] duration-300',
                    isDesktopSidebarExpanded ? DESKTOP_CONTENT_EXPANDED_OFFSET_CLASS : DESKTOP_CONTENT_COLLAPSED_OFFSET_CLASS
                )}>
                    <Outlet />
                    {!isPageWithEmbeddedIframe && (
                        <Footer />
                    )}
                    <FloatingShortcutButtons />
                </div>

            </div>
            <InviteNewUser open={inviteUserOpen} setOpen={setInviteUserOpen} />
        </>
    )
}


function Footer() {
    const { pathname } = useLocation()
    const isDashboardPage = pathname === '/dashboard'

    return (
        <footer className="mt-auto p-4 flex gap-4 items-center justify-center flex-col max-sm:text-center relative">

            <ul className="flex text-sm gap-y-2 gap-x-8 max-sm:flex-col">
                <Menu>
                    {({ open, close }) => (
                        <li
                            onMouseLeave={close}
                        >
                            <MenuButton className="link font-medium cursor-pointer"
                                onMouseEnter={({ currentTarget }) => open ? "" : currentTarget.click()}
                            >
                                {t("contactUs")}
                            </MenuButton>
                            <MenuItems modal={false} anchor="bottom" transition
                                className="z-10 transition duration-200 data-[closed]:translate-y-2 data-[closed]:opacity-0 bg-white py-1.5 rounded-md shadow-lg border border-gray-100"
                            >
                                <MenuItem>
                                    <a className="block data-[focus]:bg-gray-100 text-sm px-2 py-1" href={`tel:${companyConfig.phone.company}`}>
                                        <PhoneIcon className='size-4 inline mr-1' aria-hidden="true" />
                                        {formatPhone(companyConfig.phone.company)}
                                    </a>
                                </MenuItem>
                                <MenuItem>
                                    <a className="block data-[focus]:bg-gray-100 text-sm px-2 py-1" href={`mailto:${companyConfig.emails.info}`}>
                                        <AtSymbolIcon className='size-4 inline mr-1' aria-hidden="true" />
                                        {companyConfig.emails.info}
                                    </a>
                                </MenuItem>
                            </MenuItems>

                        </li>
                    )}
                </Menu>
                <Menu>
                    {({ open, close }) => (
                        <li
                            onMouseLeave={close}
                        >
                            <MenuButton className="link font-medium cursor-pointer"
                                onMouseEnter={({ currentTarget }) => open ? "" : currentTarget.click()}
                            >
                                {t("bankInfo")}
                            </MenuButton>
                            <MenuItems modal={false} anchor="bottom" transition
                                className="z-10 transition duration-200 data-[closed]:translate-y-2 data-[closed]:opacity-0 bg-white rounded-md shadow-lg px-2 py-2.5 border border-gray-100"
                            >
                                <MenuItem>
                                    <div className='text-sm'>
                                        <strong>{companyConfig.bankInfo.name}</strong>
                                        <p>{t("bankRegistrationNumber")} {companyConfig.bankInfo.registrationNumber}</p>
                                        <p>{t("bankAccountNumber")} {companyConfig.bankInfo.accountNumber}</p>
                                    </div>
                                </MenuItem>
                            </MenuItems>

                        </li>
                    )}
                </Menu>
                <li><a className='link font-medium' target="_blank" href={companyConfig.links.subscriptionTerms}>{t("subscriptionTerms")}</a></li>
                <li className='link font-medium cursor-pointer'><CookieDeclarationButton /></li>

            </ul>
            {!isDashboardPage && (
                <Menu>
                    <MenuButton className="btn btn-primary max-sm:order-2 lg:absolute bottom-4 left-6">
                        <EnvelopeIcon className="size-5" aria-hidden="true" />
                        {t("sendMessage")}
                    </MenuButton>
                    <MenuItems modal={false} anchor="bottom start" transition
                        className="z-10 [--anchor-gap:8px] [--anchor-padding:12px] transition sm:w-[360px] max-sm:inset-x-3 duration-200 data-[closed]:translate-y-2 data-[closed]:opacity-0 bg-white rounded-md shadow-lg border border-gray-100"
                    >
                        <MenuSection>
                            <MenuHeading className="text-sm px-3 py-1 font-medium text-dark-blue">{t("messageToSupport")}</MenuHeading>
                            <MenuSeparator className="h-px bg-gray-200" />
                            <div className="p-3">
                                <p className='text-sm'>{t("content:support.Send a message to our support. Your message will be sent directly to our support, with information about you and your case. We look forward to hearing from you.")}</p>
                                <MenuSeparator className="my-4 h-px bg-gray-200" />
                                <SupportMessageForm stopTextareaKeyDownPropagation />
                            </div>
                        </MenuSection>
                    </MenuItems>
                </Menu>
            )}
            <small className="whitespace-nowrap max-sm:order-3">{t("companyCopyright")}</small>
        </footer>
    )
}

function DesktopSidebar({ isExpanded, onToggle }: { isExpanded: boolean, onToggle: () => void }) {
    return (
        <div className="max-lg:hidden lg:fixed inset-y-0 z-40">
            <div className={clsx(
                'flex w-full h-full flex-col max-lg:px-4 bg-dark-blue-hover shadow-lg shadow-dark-blue transition-[width,padding] duration-300 overflow-x-clip',
                isExpanded
                    ? `${DESKTOP_SIDEBAR_EXPANDED_WIDTH_CLASS} lg:px-3`
                    : `${DESKTOP_SIDEBAR_COLLAPSED_WIDTH_CLASS} lg:items-center`
            )}>
                <Link to={'/dashboard'} className='self-center mt-4 mb-6'>
                    <img
                        className='h-auto w-12'
                        src={DMFPROIconLight}
                        alt={t("dmfLogo")}
                    />
                </Link>
                <HubMenu isExpanded={isExpanded} />
                <SidebarProfileSummary
                    className={clsx('mb-2', !isExpanded && 'justify-center px-0')}
                    showDetails={isExpanded}
                />
                <Button
                    type="button"
                    onClick={onToggle}
                    className={clsx(
                        'btn mb-4 bg-white/10 text-white hover:bg-white/20 active:bg-white/20 border border-white/10 size-10 p-0 justify-center',
                        isExpanded ? 'self-end' : 'self-center'
                    )}
                >
                    <span className="sr-only">{isExpanded ? t("collapseSidebar") : t("expandSidebar")}</span>
                    {isExpanded ? (
                        <ChevronLeftIcon className="size-5" aria-hidden="true" />
                    ) : (
                        <ChevronRightIcon className="size-5" aria-hidden="true" />
                    )}
                </Button>
            </div>
        </div >
    )
}

function MobileSidebar({ setInviteUserOpen }: { setInviteUserOpen: InviteUserOpenSetter }) {
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const location = useLocation()
    useEffect(() => {
        setSidebarOpen(false)
    }, [location.state.key])
    return (<>
        <div className='fixed top-0 z-40 lg:hidden flex items-center gap-4'>
            <Button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className='btn btn-gray rounded-none bg-transparent min-w-16 h-16 px-4 flex items-center justify-center'
            >
                <span className="sr-only">{t("openSidebar")}</span>
                <Bars3Icon className='size-8' />
            </Button>
            <SubLogo />
        </div>

        <Transition show={sidebarOpen}>
            <Dialog className="relative z-[2147483001] lg:hidden" onClose={setSidebarOpen}>
                <TransitionChild>
                    <div className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 data-[closed]:opacity-0" />
                </TransitionChild>

                <div className="fixed inset-0 flex">
                    <TransitionChild>
                        <DialogPanel className="relative pr-14 flex w-full max-w-96 flex-1 transition-transform duration-300 data-[closed]:-translate-x-full">
                            <TransitionChild>
                                <div className="absolute right-0 top-0 flex w-14 justify-center pt-5 transition-opacity duration-300 data-[closed]:opacity-0">
                                    <Button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                                        <span className="sr-only">{t("closeSidebar")}</span>
                                        <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                                    </Button>
                                </div>
                            </TransitionChild>

                            <div className="flex w-full h-full flex-col max-lg:px-4 bg-dark-blue-hover overflow-y-auto overflow-x-clip">
                                <img
                                    className="h-8 object-contain object-left my-4"
                                    src={DMFPROLogoWhite}
                                    alt={t("dmfLogo")}
                                />
                                <div className='py-4'>
                                    <SearchBar />
                                </div>
                                <HubMenu />
                                <MobileProfileNav
                                    setInviteUserOpen={setInviteUserOpen}
                                    onCloseMenu={() => setSidebarOpen(false)}
                                />
                            </div>

                        </DialogPanel>
                    </TransitionChild>
                </div>
            </Dialog>
        </Transition>
    </>
    )
}

function AuthHeader({
    isDesktopSidebarExpanded,
    setInviteUserOpen,
}: {
    isDesktopSidebarExpanded: boolean;
    setInviteUserOpen: InviteUserOpenSetter;
}) {
    const { user } = useLoaderData({ from: "/_protected" })

    return (
        <>
            {/* <div className={browserType === "other" ? 'pt-0' : 'pt-6'} /> */}
            <div className="max-lg:hidden z-40 fixed w-full top-0">
                <SystemNews />

                <div className={clsx(
                    'h-20 bg-white border-b flex justify-between items-center transition-[padding] duration-300',
                    isDesktopSidebarExpanded ? DESKTOP_CONTENT_EXPANDED_OFFSET_CLASS : DESKTOP_CONTENT_COLLAPSED_OFFSET_CLASS
                )}>
                    <SubLogo className="pl-6" />
                    <SearchBar />

                    <DesktopRecommendations setInviteUserOpen={setInviteUserOpen} />

                    <div className='flex items-center gap-2 lg:pl-6'>
                        <Button type="button" className={'btn btn-gray-outline'} onClick={() => setInviteUserOpen(true)}>
                            <UserPlusIcon className={'size-4'} />
                            {t("inviteColleague")}
                        </Button>

                        <CreateNewCase user={user} />
                    </div>

                </div>
            </div>
        </>
    )
}


function MobileProfileNav({
    setInviteUserOpen,
    onCloseMenu,
}: {
    setInviteUserOpen: InviteUserOpenSetter;
    onCloseMenu: () => void;
}) {
    const logout = useLogout()

    async function onLogout() {
        try {
            await logout()
        } catch (error) {
            errorMessage(error)
        }
    }

    return (
        <nav className="pb-4 pt-8">

            <MobileRecommendations
                setInviteUserOpen={setInviteUserOpen}
                onCloseMenu={onCloseMenu}
            />

            <SidebarProfileSummary />

            <ul role="list" className="flex flex-1 flex-col gap-2 pt-2">
                <li>
                    <Link
                        to="/dealership/profile"
                        className="nav-list-button"
                    >
                        <UserIcon className='nav-list-icon' aria-hidden="true" />
                        <span className='text-white/80 group-hover:text-white group-[.current]:text-white font-semibold text-center text-sm/3 uppercase'>{t("my") + " " + t("profile").toLowerCase()}</span>
                    </Link>
                </li>
                <li>
                    <Link
                        to="/dealership"
                        className="nav-list-button"
                    >
                        <BuildingOfficeIcon className='nav-list-icon' aria-hidden="true" />
                        <span className='text-white/80 group-hover:text-white group-[.current]:text-white font-semibold text-center text-sm/3 uppercase'>{t("my") + " " + t("company").toLowerCase()}</span>
                    </Link>
                </li>
                <li>
                    <Button
                        className="nav-list-button"
                        onClick={onLogout}
                    >
                        <ArrowRightStartOnRectangleIcon className='nav-list-icon' aria-hidden="true" />
                        <span className='text-white/80 group-hover:text-white group-[.current]:text-white font-semibold text-center text-sm/3 uppercase'>{t("logout")}</span>
                    </Button>
                </li>
            </ul>
        </nav>
    )
}

function SidebarProfileSummary({
    className,
    showDetails = true,
}: {
    className?: string;
    showDetails?: boolean;
}) {
    const { data: profile } = useProfile()
    const { user } = useLoaderData({ from: "/_protected" })
    const userName = profile?.data.name
    const userEmail = profile?.data.email
    const avatar = profile?.data.avatar?.uri

    return (
        <div className={clsx("flex items-center h-16 gap-2 px-4", className)}>
            <span className="relative">
                <Avatar
                    imgSrc={avatar}
                    size='md'
                />
                <span className='bg-primary rounded-full font-semibold px-1 py-0.5 text-white inline-flex items-center gap-1 absolute -bottom-2 left-1/2 -translate-x-1/2'
                    title={`${user?.credits.left} ${t("clipsLeft")}`}
                >
                    <TicketIcon className='size-4' />
                    <span className='text-xxs'>
                        {(user?.credits.left && user.credits.left > 99) ? '99+' : user?.credits.left}
                    </span>
                </span>
            </span>
            {showDetails && (
                <div className="ml-2 flex-1 truncate mr-2">
                    <div className="text-sm font-medium text-white truncate ">{userName}</div>
                    <div className="text-xs font-medium text-text-primary truncate">{userEmail}</div>
                </div>
            )}
        </div>
    )
}


function HubMenu({ isExpanded = false }: { isExpanded?: boolean }) {
    const logout = useLogout()
    const path = useRouterState({ select: state => state.location.pathname })
    const { user } = useLoaderData({ from: "/_protected" })

    async function onLogout() {
        try {
            await logout()
        } catch (error) {
            errorMessage(error)
        }
    }

    return (
        <nav className="flex flex-1 flex-col w-full">
            <ul role="list" className="flex flex-1 flex-col gap-2">
                <li>
                    <Link
                        to='/dashboard'
                        className={clsx(
                            "nav-list-button group",
                            isExpanded && "lg:w-full lg:flex-row lg:justify-start"
                        )}
                        activeProps={{ className: "current" }}
                        activeOptions={{ exact: true }}
                    >
                        <DashboardIcon className='flex-shrink-0' />
                        <span className={clsx(
                            'text-white/80 group-hover:text-white group-[.current]:text-white font-semibold uppercase transition-all duration-300 text-sm leading-none',
                            !isExpanded && 'lg:hidden'
                        )}>
                            {t("dashboard")}
                        </span>
                    </Link>
                </li>
                <li>
                    <Link
                        to='/tax'
                        search={{ page: 1 }}
                        className={clsx(
                            "nav-list-button group",
                            path.includes('/tax') && "current",
                            isExpanded && "lg:w-full lg:flex-row lg:justify-start"
                        )}
                    >
                        <TAXIcon className='flex-shrink-0' />
                        <span className={clsx(
                            'text-white/80 group-hover:text-white group-[.current]:text-white font-semibold uppercase transition-all duration-300 text-sm leading-none',
                            !isExpanded && 'lg:hidden'
                        )}>
                            {t("tax")}
                        </span>
                    </Link>
                </li>

                {user.products.vehicleReport && (
                    <li>
                        <Link
                            to='/equipment'
                            className={clsx(
                                "nav-list-button group",
                                path.includes('/equipment') && "current",
                                isExpanded && "lg:w-full lg:flex-row lg:justify-start"
                            )}
                        >
                            <EquipmentIcon className='flex-shrink-0' aria-hidden="true" />
                            <span className={clsx(
                                'text-white/80 group-hover:text-white group-[.current]:text-white font-semibold uppercase transition-all duration-300 text-sm leading-none',
                                !isExpanded && 'lg:hidden'
                            )}>
                                {t("equipment")}
                            </span>
                        </Link>
                    </li>
                )}

                {user.products.taxLegal && (
                    <li>
                        <Link
                            to='/legal'
                            className={clsx(
                                "nav-list-button group",
                                path.includes('/legal') && "current",
                                isExpanded && "lg:w-full lg:flex-row lg:justify-start"
                            )}
                        >
                            <ScaleIcon className='flex-shrink-0 w-6 h-6' aria-hidden="true" />
                            <span className={clsx(
                                'text-white/80 group-hover:text-white group-[.current]:text-white font-semibold uppercase transition-all duration-300 text-sm leading-none',
                                !isExpanded && 'lg:hidden'
                            )}>
                                Legal
                            </span>
                        </Link>
                    </li>
                )}

                {(user.products.leasing || user.products.import) && (
                    <hr className={clsx('border-white/10 border-t-2', isExpanded ? 'mx-2 my-3' : 'my-3')} />
                )}
                {user.products.leasing && (
                    <li>
                        <Link
                            to='/leasing'
                            className={clsx(
                                "nav-list-button group",
                                path.includes('/leasing') && "current",
                                isExpanded && "lg:w-full lg:flex-row lg:justify-start"
                            )}
                        >
                            <LEASINGIcon className='flex-shrink-0' aria-hidden="true" />
                            <span className={clsx(
                                'text-white/80 group-hover:text-white group-[.current]:text-white font-semibold uppercase transition-all duration-300 text-sm leading-none',
                                !isExpanded && 'lg:hidden'
                            )}>
                                {t("leasing")}
                            </span>
                        </Link>
                    </li>
                )}
                {user.products.import && (
                    <li>
                        <Link
                            to='/import'
                            className={clsx(
                                "nav-list-button group",
                                path.includes('/import') && "current",
                                isExpanded && "lg:w-full lg:flex-row lg:justify-start"
                            )}
                        >
                            <IMPORTIcon className='flex-shrink-0' aria-hidden="true" />
                            <span className={clsx(
                                'text-white/80 group-hover:text-white group-[.current]:text-white font-semibold uppercase transition-all duration-300 text-sm leading-none',
                                !isExpanded && 'lg:hidden'
                            )}>
                                {t("import")}
                            </span>
                        </Link>
                    </li>
                )}

                <hr className={clsx('hidden border-white/10 border-t-2 lg:block', isExpanded ? 'mx-2 my-3' : 'my-3')} />
                <li className='hidden lg:block'>
                    <Link
                        to='/dealership/profile'
                        className={clsx(
                            "nav-list-button group",
                            isExpanded && "lg:w-full lg:flex-row lg:justify-start"
                        )}
                        activeProps={{ className: "current" }}
                        activeOptions={{ exact: true }}
                    >
                        <UserIcon className='flex-shrink-0' aria-hidden="true" />
                        <span className={clsx(
                            'text-white/80 group-hover:text-white group-[.current]:text-white font-semibold uppercase transition-all duration-300 text-sm leading-none',
                            !isExpanded && 'lg:hidden'
                        )}>
                            {t("my")} {t("profile").toLowerCase()}
                        </span>
                    </Link>
                </li>
                <li className='hidden lg:block'>
                    <Link
                        to='/dealership'
                        className={clsx(
                            "nav-list-button group",
                            isExpanded && "lg:w-full lg:flex-row lg:justify-start"
                        )}
                        activeProps={{ className: "current" }}
                        activeOptions={{ exact: true }}
                    >
                        <BuildingOfficeIcon className='flex-shrink-0' aria-hidden="true" />
                        <span className={clsx(
                            'text-white/80 group-hover:text-white group-[.current]:text-white font-semibold uppercase transition-all duration-300 text-sm leading-none',
                            !isExpanded && 'lg:hidden'
                        )}>
                            {t("my")} {t("company").toLowerCase()}
                        </span>
                    </Link>
                </li>
                <li className='hidden lg:block'>
                    <Button
                        className={clsx(
                            "nav-list-button group",
                            isExpanded && "lg:w-full lg:flex-row lg:justify-start"
                        )}
                        onClick={onLogout}
                    >
                        <ArrowRightStartOnRectangleIcon className='flex-shrink-0' aria-hidden="true" />
                        <span className={clsx(
                            'text-white/80 group-hover:text-white group-[.current]:text-white font-semibold uppercase transition-all duration-300 text-sm leading-none',
                            !isExpanded && 'lg:hidden'
                        )}>
                            {t("logout")}
                        </span>
                    </Button>
                </li>


            </ul>
        </nav>
    )
}


function SearchBar() {
    const [query, setQuery] = useState("")
    const [showResults, setShowResults] = useState(false);
    const parentRef = useRef<HTMLDivElement>(null)
    const { data, isFetching } = useQuery({
        queryKey: ['globalSearch', query],
        queryFn: async () => {
            setShowResults(false)
            const data = await searchSystem(query)
            setShowResults(true)
            return data.data
        },
        enabled: !!query,
    })
    const hasData = !!data?.data.tax || !!data?.data.import || !!data?.data.leasing || !!data?.data.user;

    const doDebounceSearch = useCallback(
        debounce((e: ChangeEvent<HTMLInputElement>) =>
            setQuery(() => e.target.value)
            , 650),
        []
    )
    function onSearchClick() {
        if (hasData) {
            setShowResults(true)
        }
    }

    useEffect(() => {
        function listenForClicksAway(e: MouseEvent | TouchEvent) {
            if (parentRef.current && parentRef.current.contains(e.target as Node)) {
                return
            }
            setShowResults(false)
        }
        window.addEventListener('mousedown', listenForClicksAway)
        window.addEventListener('touchstart', listenForClicksAway)

        return () => {
            window.removeEventListener('mousedown', listenForClicksAway)
            window.removeEventListener('touchstart', listenForClicksAway)
        }
    }, [showResults])

    useEffect(() => {
        if (hasData) {
            setShowResults(true)
        } else {
            setShowResults(false)
        }
    }, [data])

    return (
        <div className="flex-1 lg:pl-6 relative" ref={parentRef}>
            <label htmlFor="search" className="sr-only">
                {t("search")}
            </label>
            <div className="relative ">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 max-lg:text-white">
                    {isFetching ? (
                        <ArrowPathIcon className="animate-spin h-5 w-5" aria-hidden="true" />
                    ) : (
                        <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
                    )}
                </div>
                <input
                    onChange={doDebounceSearch}
                    className={clsx(
                        "block w-full rounded-md border-0 pl-10 pr-3 placeholder:text-gray-400 focus:ring-0 min-h-10",
                        "lg:text-sm lg:leading-10 lg:bg-gray-50",
                        "bg-white/[2%] max-lg:focus:bg-white/10 max-lg:hover:bg-white/10 max-lg:transition-colors max-lg:text-white"
                    )}
                    placeholder={t("searchPlaceholder")}
                    type="search"
                    onClick={onSearchClick}
                />
                <Transition show={showResults}>
                    <button
                        type="button"
                        aria-label={t('close')}
                        className="fixed inset-0 z-50 bg-dark-blue/30 transition duration-200 data-[closed]:opacity-0"
                        onClick={() => setShowResults(false)}
                    />
                </Transition>
                <Transition show={showResults}>
                    <nav className={clsx(
                        'z-50 border rounded-md backdrop-blur-2xl',
                        "lg:absolute lg:bg-white lg:top-full lg:w-full",
                        "bg-white/20 fixed max-lg:top-[156px] max-lg:bottom-4 max-lg:inset-x-4 max-lg:border-white/30"
                    )}>
                        <ul className="flex flex-col overflow-y-auto p-6 min-h-20 lg:max-h-96 max-h-full">
                            <li className={clsx(window.location.pathname.includes("tax") ? "order-first" : "order-1")}>
                                {data?.data?.tax && data?.data?.tax.length > 0 && (
                                    <div className='flex items-center justify-between gap-2 mb-3 flex-wrap lg:px-1'>
                                        <p className='text-dark-blue max-lg:text-white font-medium'>{t("taxCases")}</p>
                                        <Link to={"/tax"} className='text-xs underline max-lg:text-white'>
                                            {t("viewAllTaxCases")}
                                        </Link>
                                    </div>
                                )}
                                <ul>
                                    {data?.data?.tax?.map((link, i) => (
                                        <li key={i} className="max-lg:mt-2 max-lg:bg-gray-300 max-lg:rounded-md max-lg:px-3">
                                            <a href={link.uri}
                                                className="flex items-center flex-wrap gap-3 justify-between border-b lg:lg:hover:bg-gray-100/50 lg:px-1 lg:focus:bg-gray-100 focus:bg-gray-100/50 py-3 text-xs max-lg:text-gray-600"
                                            >
                                                <p><span className='text-dark-blue font-medium'>{link.name}</span> - {link.resourceId}</p>
                                                <StatusTag name={link.status?.name} value={link.status?.value} />
                                            </a>
                                        </li>
                                    ))}

                                </ul>
                            </li>
                            <li className={clsx(window.location.pathname.includes("import") ? "order-first" : "order-2", "mt-5")}>
                                {data?.data?.import && data?.data?.import.length > 0 && (
                                    <div className='flex items-center flex-wrap justify-between gap-2 mb-3'>
                                        <p className='text-dark-blue max-lg:text-white font-medium'>{t("importCases")}</p>
                                        <Link to={"/import"} className='text-xs underline max-lg:text-white'>
                                            {t("viewAllImportCases")}
                                        </Link>
                                    </div>
                                )}
                                <ul>
                                    {data?.data?.import?.map((link, i) => (
                                        <li key={i} className="max-lg:mt-2 max-lg:bg-gray-300 max-lg:rounded-md max-lg:px-3">
                                            <a href={link.uri}
                                               className="flex items-center flex-wrap gap-3 justify-between border-b lg:hover:bg-gray-100/50 lg:focus:bg-gray-100 focus:bg-gray-100/50 py-3 text-xs max-lg:text-gray-600"
                                            >
                                                <p><span className='text-dark-blue font-medium'>{link.name}</span> - {link.resourceId}</p>
                                                <StatusTag name={link.status?.name} value={link.status?.value} />
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </li>
                            <li className={clsx("mt-5", window.location.pathname.includes("leasing") ? "order-first" : "order-3")}>
                                {data?.data?.leasing && data?.data?.leasing.length > 0 && (
                                    <div className='flex items-center flex-wrap justify-between gap-2 mb-3'>
                                        <p className='text-dark-blue max-lg:text-white font-medium'>{t("leasingCases")}</p>
                                        <Link to={"/leasing"} className='text-xs underline max-lg:text-white'>
                                            {t("viewAllLeasingCases")}
                                        </Link>
                                    </div>
                                )}
                                <ul>
                                    {data?.data?.leasing?.map((link, i) => (
                                        <li key={i} className="max-lg:mt-2 max-lg:bg-gray-300 max-lg:rounded-md max-lg:px-3">
                                            <a href={link.uri}
                                               className="flex items-center flex-wrap gap-3 justify-between border-b lg:hover:bg-gray-100/50 lg:focus:bg-gray-100 focus:bg-gray-100/50 py-3 text-xs max-lg:text-gray-600"
                                            >
                                                <p><span className='text-dark-blue font-medium'>{link.name}</span> - {link.resourceId}</p>
                                                <StatusTag name={link.status?.name} value={link.status?.value} />
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </li>
                            <li className={clsx("mt-5", window.location.pathname.includes("dealership") ? "order-first" : "order-4")}>
                                {data?.data?.user && data?.data?.user.length > 0 && (
                                    <div className='flex items-center flex-wrap justify-between gap-2 mb-3'>
                                        <p className='text-dark-blue max-lg:text-white font-medium'>{t("users")}</p>
                                    </div>
                                )}
                                <ul>
                                    {data?.data?.user?.map((link, i) => (
                                        <li key={i} className="max-lg:mt-2 max-lg:bg-gray-300 max-lg:rounded-md max-lg:px-3">
                                            <a href={link.uri}
                                               className="flex items-center flex-wrap gap-3 justify-between border-b lg:hover:bg-gray-100/50 lg:focus:bg-gray-100 focus:bg-gray-100/50 py-3 text-xs max-lg:text-gray-600"
                                            >
                                                <p><span className='text-dark-blue font-medium'>{link.name}</span> - {link.resourceId}</p>
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        </ul>
                    </nav>
                </Transition>
            </div >
        </div >
    )
}

function CreateNewCase({user}: {user: User}) {
    return (
        <div className="hidden lg:relative pr-6 lg:flex lg:items-center">
            <Menu as="div" className="relative flex-shrink-0">
                <MenuButton className="relative btn btn-primary">
                    <PlusIcon className="size-3" />
                    {t("createNewCase")}
                </MenuButton>

                <MenuItems modal={false}
                           transition
                           className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none transform-gpu transition duration-75 data-[closed]:opacity-0 data-[closed]:scale-95 data-[closed]:-translate-y-4"
                >
                    <MenuItem >
                        <Link
                            to="/tax/create"
                            className="block px-4 py-2 text-sm font-medium data-[focus]:bg-gray-100 "
                        >
                            {t("tax")}
                        </Link>
                    </MenuItem>
                    {user.products.import && (
                        <MenuItem >
                            <Link
                                to="/import/create"
                                className="block px-4 py-2 text-sm font-medium data-[focus]:bg-gray-100 "
                            >
                                {t("import")}
                            </Link>
                        </MenuItem>
                    )}
                    {user.products.vehicleReport && (
                        <MenuItem >
                            <Link
                                to="/equipment"
                                className="block px-4 py-2 text-sm font-medium data-[focus]:bg-gray-100 "
                            >
                                {t("equipment")}
                            </Link>
                        </MenuItem>
                    )}
                    {user.products.taxLegal && (
                        <MenuItem>
                            <Link
                                to="/legal/create"
                                className="block px-4 py-2 text-sm font-medium data-[focus]:bg-gray-100 "
                            >
                                Legal
                            </Link>
                        </MenuItem>
                    )}
                </MenuItems>
            </Menu>
        </div>
    )
}

function RecommendationsTrigger({ unreadCount }: { unreadCount: number }) {
    return (
        <div className='flex items-center gap-2'>
            <div className='size-4 rounded-full bg-gray-100 flex justify-center items-center lg:hidden xl:flex'>
                <span className='size-2 rounded-full bg-primary'></span>
            </div>
            <p className='text-white text-sm lg:hidden xl:block xl:text-dark-blue'>
                {unreadCount > 0 ? t("newRecommendationsForYouWithCount", { count: unreadCount }) : t("newRecommendationsForYou")}
            </p>
            <span className='hidden lg:inline-flex xl:hidden w-6 h-6 px-2 items-center justify-center rounded-full border border-primary bg-white text-primary text-xs font-semibold'>
                {unreadCount}
            </span>
        </div>
    )
}

function RecommendationList({
    recommendations,
    readIds,
    onOpenRecommendation,
}: {
    recommendations: RecommendationStateItem[];
    readIds: string[];
    onOpenRecommendation: (recommendation: RecommendationStateItem) => void;
}) {
    return (
        <div className='divide-y divide-gray-200'>
            {recommendations.map(recommendation => (
                <RecommendationListItem
                    key={recommendation.id}
                    recommendation={recommendation}
                    isRead={readIds.includes(recommendation.id)}
                    onOpen={onOpenRecommendation}
                />
            ))}
        </div>
    )
}

const RecommendationListItem = forwardRef<HTMLButtonElement, RecommendationListItemProps>(function RecommendationListItem({
    recommendation,
    isRead,
    onOpen,
    className,
    onClick,
    ...props
}, ref) {
    return (
        <button
            {...props}
            ref={ref}
            type="button"
            onClick={(event) => {
                onClick?.(event)

                if (!event.defaultPrevented) {
                    onOpen(recommendation)
                }
            }}
            className={clsx(
                'flex w-full items-start gap-4 p-4 text-left transition-colors hover:bg-gray-50',
                !isRead && 'bg-blue-50',
                className
            )}
        >
            <img
                src={recommendation.imageUrl}
                className={clsx('mt-1 size-10 shrink-0 rounded-full bg-gray-100', recommendation?.imageObjectFitContain ? 'p-1 object-contain' : 'object-cover')}
            />
            <div className='min-w-0 flex-1'>
                <div className='flex items-start justify-between gap-3'>
                    <p className='text-sm font-medium text-dark-blue'>{recommendation.title}</p>
                    {!isRead && <span className='mt-2 size-2 shrink-0 rounded-full bg-primary'></span>}
                </div>

                <p className='mt-1 text-xs'>{recommendation.summary}</p>
                <div className='mt-2 flex items-center gap-2 text-xs text-gray-500'>
                    <CalendarDaysIcon className='size-4 shrink-0' aria-hidden='true' />
                    <span>{formatDate(recommendation.date)}</span>
                </div>
            </div>
        </button>
    )
})

function RecommendationDetailsDialog({
    recommendation,
    onClose,
    setInviteUserOpen,
    onInviteUser,
}: RecommendationDetailsDialogProps) {
    const [visibleRecommendation, setVisibleRecommendation] = useState<RecommendationStateItem | null>(recommendation)

    useEffect(() => {
        if (recommendation) {
            setVisibleRecommendation(recommendation)
        }
    }, [recommendation])

    return (
        <Transition show={recommendation !== null} afterLeave={() => setVisibleRecommendation(null)}>
            <Dialog className="relative z-[2147483001] focus:outline-none" onClose={onClose}>
                <DialogBackdrop transition className="fixed inset-0 bg-dark-blue/30 backdrop-[1px]" />

                <div className="fixed bottom-8 top-32 z-10 w-screen">
                    <div className="flex h-full items-center justify-center p-4">
                        <TransitionChild>
                            <DialogPanel className="relative w-full max-w-2xl max-h-full overflow-y-auto rounded-lg bg-white p-8 duration-300 ease-out data-[closed]:transform-[scale(95%)] data-[closed]:opacity-0">
                                {visibleRecommendation && (
                                    <>
                                        <Button onClick={onClose} className="btn btn-gray absolute right-2 top-2 size-10 p-0">
                                            <span className="sr-only">{t("closeRecommendation")}</span>
                                            <XMarkIcon className="size-5" aria-hidden="true" />
                                        </Button>

                                        <div className='mb-4'>
                                            <div className='mt-2 flex items-start gap-4'>
                                                <img
                                                    src={visibleRecommendation.imageUrl}
                                                    alt=""
                                                    className={clsx('size-16 shrink-0 rounded-full', recommendation?.imageObjectFitContain ? 'p-1 object-contain' : 'object-cover')}
                                                />
                                                <div>
                                                    <div className='flex items-center gap-2 text-xs text-gray-500'>
                                                        <CalendarDaysIcon className='size-4 shrink-0' aria-hidden='true' />
                                                        <span>{formatDate(visibleRecommendation.date)}</span>
                                                    </div>

                                                    <p className='text-lg font-medium text-dark-blue'>{visibleRecommendation.title}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <p className='text-sm text-gray-700 whitespace-pre-line'>{visibleRecommendation.body}</p>

                                        {((visibleRecommendation.link && visibleRecommendation.linkText) ||
                                            (visibleRecommendation.secondaryLink && visibleRecommendation.secondaryLinkText)) && (
                                            <div className='mt-6 flex flex-wrap gap-3'>
                                                {(visibleRecommendation.link && visibleRecommendation.linkText) && (
                                                    <Link
                                                        to={visibleRecommendation.link}
                                                        className='btn btn-primary'
                                                        target={visibleRecommendation.target || '_self'}
                                                        onClick={visibleRecommendation.inviteUser ? () => {
                                                            setInviteUserOpen(true)
                                                            onInviteUser?.()
                                                            onClose()
                                                            setVisibleRecommendation(null)
                                                        } : undefined}
                                                    >
                                                        {visibleRecommendation.linkText}
                                                    </Link>
                                                )}
                                                {(visibleRecommendation.secondaryLink && visibleRecommendation.secondaryLinkText) && (
                                                    <Link
                                                        to={visibleRecommendation.secondaryLink}
                                                        className='btn btn-gray-outline'
                                                        target={visibleRecommendation.secondaryTarget || '_self'}
                                                    >
                                                        {visibleRecommendation.secondaryLinkText}
                                                    </Link>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}

function DesktopRecommendations({ setInviteUserOpen }: { setInviteUserOpen: InviteUserOpenSetter }) {
    const { user } = useLoaderData({ from: "/_protected" })
    const recommendations = getRecommendations(user);
    const {
        readIds,
        selectedRecommendation,
        setSelectedRecommendation,
        openRecommendation,
        unreadCount,
    } = useRecommendationsState({
        recommendations,
        userId: user.id,
    })

    return (
        <>
            <Popover as="div" className="relative hidden lg:block lg:ml-4">
                {({ close }) => (
                    <>
                        <PopoverButton>
                            <RecommendationsTrigger unreadCount={unreadCount} />
                        </PopoverButton>

                        <PopoverPanel
                            transition
                            className="absolute -left-40 xl:left-0 z-10 mt-2 w-[500px] origin-top-left rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none transform-gpu transition duration-75 data-[closed]:opacity-0 data-[closed]:scale-95 data-[closed]:-translate-y-4"
                        >
                            <RecommendationList
                                recommendations={recommendations}
                                readIds={readIds}
                                onOpenRecommendation={(recommendation) => {
                                    close()
                                    openRecommendation(recommendation)
                                }}
                            />
                        </PopoverPanel>
                    </>
                )}
            </Popover>

            <RecommendationDetailsDialog
                recommendation={selectedRecommendation}
                setInviteUserOpen={setInviteUserOpen}
                onClose={() => setSelectedRecommendation(null)}
            />
        </>
    )
}

function MobileRecommendations({
    setInviteUserOpen,
    onCloseMenu,
}: {
    setInviteUserOpen: InviteUserOpenSetter;
    onCloseMenu: () => void;
}) {
    const [isOpen, setIsOpen] = useState(false)
    const { user } = useLoaderData({ from: "/_protected" })
    const recommendations = getRecommendations(user);
    const {
        readIds,
        selectedRecommendation,
        setSelectedRecommendation,
        openRecommendation,
        unreadCount,
    } = useRecommendationsState({
        recommendations,
        userId: user.id,
    })

    return (
        <>
            <div className="mb-4 px-4 xl:hidden">
                <Button type="button" onClick={() => setIsOpen(true)}>
                    <RecommendationsTrigger unreadCount={unreadCount} />
                </Button>

                <Transition show={isOpen}>
                    <Dialog className="relative z-[2147483001] lg:hidden" onClose={setIsOpen}>
                        <TransitionChild>
                            <div className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 data-[closed]:opacity-0" />
                        </TransitionChild>

                        <div className="fixed inset-0 flex items-end">
                            <TransitionChild>
                                <DialogPanel className="w-full rounded-t-2xl bg-white pt-4 transition-transform max-h-full overflow-y-auto duration-300 data-[closed]:translate-y-full">
                                    <div className="mb-2 flex items-center justify-between gap-4 px-4">
                                        <div>
                                            <p className="text-dark-blue font-medium">{t("recommendationsLabel")}</p>
                                        </div>
                                        <Button type="button" className="-m-2.5 p-2.5" onClick={() => setIsOpen(false)}>
                                            <span className="sr-only">{t("closeRecommendations")}</span>
                                            <XMarkIcon className="size-6 text-dark-blue" aria-hidden="true" />
                                        </Button>
                                    </div>

                                    <RecommendationList
                                        recommendations={recommendations}
                                        readIds={readIds}
                                        onOpenRecommendation={(recommendation) => {
                                            setIsOpen(false)
                                            openRecommendation(recommendation)
                                        }}
                                    />
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </Dialog>
                </Transition>
            </div>

            <RecommendationDetailsDialog
                recommendation={selectedRecommendation}
                setInviteUserOpen={setInviteUserOpen}
                onInviteUser={onCloseMenu}
                onClose={() => setSelectedRecommendation(null)}
            />
        </>
    )
}

function SystemNews() {
    const [isActive, setIsActive] = useState(false)
    const {data: urgentSystemNews, dismissUrgentSystemNews} = useUrgentSystemNews()

    function closeArticle() {
        setIsActive(false)
    }

    return (
        <>
            {urgentSystemNews && (
                <div className='bg-primary text-center text-white py-1 text-sm flex flex-wrap justify-center items-center gap-2'>
                    <p>{urgentSystemNews.title}</p>
                    <Button className='underline' onClick={() => setIsActive(true)}>
                        {t('readMore')}
                    </Button>
                    <Button type="button" aria-label={t('close')} className="rounded p-1 transition hover:bg-white/10" onClick={dismissUrgentSystemNews}>
                        <XMarkIcon className="size-4" aria-hidden="true" />
                    </Button>
                </div>
            )}

            {urgentSystemNews && (
                <Dialog open={isActive} as="div" className="relative z-50 focus:outline-none" onClose={closeArticle}>
                    <DialogBackdrop transition className="fixed inset-0 bg-dark-blue/30 backdrop-[1px]" />
                    <div className="fixed bottom-8 top-32 z-10 w-screen ">
                        <div className="flex h-full items-center justify-center p-4 ">
                            <DialogPanel
                                transition
                                className="relative w-full max-w-2xl max-h-full overflow-y-auto rounded-lg bg-white p-8 duration-300 ease-out data-[closed]:transform-[scale(95%)] data-[closed]:opacity-0"
                            >
                                <Button aria-label={t('close')} onClick={closeArticle} className="btn btn-gray absolute right-2 top-2 size-10 p-0"><XMarkIcon className="size-5" aria-hidden="true" /></Button>
                                <div className='space-y-4'>
                                    <h2 className='text-2xl font-medium text-dark-blue'>{urgentSystemNews.title}</h2>
                                    {urgentSystemNews.date && (
                                        <p className='text-sm'>{formatDate(urgentSystemNews.date)}</p>
                                    )}
                                    <div
                                        className='prose max-w-none'
                                        dangerouslySetInnerHTML={{__html: urgentSystemNews.content || ''}}
                                    />
                                </div>
                            </DialogPanel>
                        </div>
                    </div>
                </Dialog>
            )}
        </>
    )
}
